import { FARE_APPLICATION_ID } from "@/lib/auth/config";
import type { ApiResponse } from "@/lib/auth/api-types";
import { api, asApiResponse, buildEnterpriseApiUrl, buildProfileApiUrl } from "@/lib/auth/http";
import type { CourseResponse } from "./api";

// LMS Enterprise domain client (:8094) — Enterprise, Enterprise Member,
// Department, Team, Enterprise Role Assignment, Enterprise Member
// Assignment, Reporting Relation, Content Access — plus a read-only lookup
// of existing Profile Types/Roles (:8087) for the role-assignment dropdown.
// Same resources/paths as documented in gully-properties-admin-portal-fe's
// docs/LMS_APP_API_AND_ROLES_INTEGRATION_GUIDE.md, ported to this app's own
// axios (`api`) + ApiResponse conventions instead of that repo's next-auth
// + React Query stack. Role/Profile Type *creation* is intentionally not
// included — out of scope for this app.

export type AuditInfo = {
  createdBy?: string;
  createdTs?: string;
  lastModifiedBy?: string;
  lastModifiedTs?: string;
};

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (Array.isArray(obj.content)) return obj.content as T[];
  }
  return [];
}

async function enterpriseRequest<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  data?: unknown,
): Promise<ApiResponse<T>> {
  return asApiResponse<T>(
    api.request<T>({ url: buildEnterpriseApiUrl(path), method, data }),
  );
}

async function enterpriseListRequest<T>(path: string): Promise<ApiResponse<T[]>> {
  const res = await enterpriseRequest<T[] | { data?: T[] }>(path, "GET");
  if (!res.success) return res as ApiResponse<T[]>;
  return { success: true, data: asArray<T>(res.data), message: "Success" };
}

async function profileRequest<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  data?: unknown,
): Promise<ApiResponse<T>> {
  return asApiResponse<T>(
    api.request<T>({ url: buildProfileApiUrl(path), method, data }),
  );
}

// ===================== Enterprise =====================

export type EnterpriseStatus = "ACTIVE" | "DISABLED" | "DRAFT";

export interface Enterprise {
  id?: string;
  businessPageId: string;
  enterpriseName: string;
  enterpriseCode: string;
  logo?: string | null;
  lmsEnabled?: boolean;
  companyAdminUserId?: string | null;
  subscriptionId: string;
  pricingPlanId: string;
  courseIds?: string[];
  status?: EnterpriseStatus;
  auditInfo?: AuditInfo;
}

export type EnterpriseCreatePayload = {
  businessPageId: string;
  enterpriseName: string;
  enterpriseCode: string;
  subscriptionId: string;
  pricingPlanId: string;
};

export type EnterpriseUpdatePayload = Partial<
  Pick<Enterprise, "enterpriseName" | "subscriptionId" | "pricingPlanId" | "lmsEnabled" | "status" | "courseIds">
>;

export const enterpriseApi = {
  list: () => enterpriseListRequest<Enterprise>("/lms/enterprises"),
  get: (id: string) => enterpriseRequest<Enterprise>(`/lms/enterprises/${id}`),
  create: (payload: EnterpriseCreatePayload) =>
    enterpriseRequest<Enterprise>("/lms/enterprises", "POST", payload),
  update: (id: string, payload: EnterpriseUpdatePayload) =>
    enterpriseRequest<Enterprise>(`/lms/enterprises/${id}`, "PUT", payload),
  // Soft-disable (204), not a hard delete.
  disable: (id: string) => enterpriseRequest<void>(`/lms/enterprises/${id}`, "DELETE"),

  // GET /lms/enterprises/{id}/courses — the real, backend-resolved list of
  // courses tagged to this enterprise (via its courseIds field). Used by
  // CoursesScreen to scope the catalogue to the current enterprise, instead
  // of the old client-side "fetch everything, filter locally" approach.
  getCourses: async (id: string): Promise<ApiResponse<CourseResponse[]>> => {
    // Response is { enterpriseId, enterpriseName, enterpriseCode, courses: [...] }
    // — not a bare array/.data/.content, so it needs its own unwrapping
    // rather than the generic asArray() helper.
    const res = await enterpriseRequest<{ courses?: CourseResponse[] } | CourseResponse[]>(
      `/lms/enterprises/${id}/courses`,
      "GET",
    );
    if (!res.success) return res as ApiResponse<CourseResponse[]>;
    const data = res.data;
    const courses = Array.isArray(data) ? data : data?.courses ?? [];
    return { success: true, data: courses, message: "Success" };
  },
};

// ===================== Current enterprise (single-tenant) =====================
// Unlike the super-admin backoffice, this app only ever operates against
// one enterprise at a time — the one the signed-in user's company actually
// uses. Picked once (Teams screen's enterprise picker) and remembered here
// so any other screen (e.g. Course creation) can tag new records to it
// without asking the user to pick from a multi-enterprise list.
const CURRENT_ENTERPRISE_STORAGE_KEY = "fare:currentEnterpriseId";

// Fired whenever the current enterprise changes (e.g. the user picks a
// different enterprise/role from the account dropdown) — screens that fetch
// enterprise-scoped data (Teams, Lessons) listen for this to refetch, the
// same window-event pattern AUTH_SESSION_EXPIRED_EVENT uses in lib/auth/http.ts.
export const ENTERPRISE_CHANGED_EVENT = "fare:enterprise-changed";

/** Sidebar catalog option — not a real enterprise id. */
export const FARE_RETAIL_ID = "fare-retail";
export const FARE_RETAIL_LABEL = "Fare Retail";

export function isFareRetailSelection(id: string = getCurrentEnterpriseId()) {
  return id === FARE_RETAIL_ID;
}

export function exclusiveEnterpriseFlag(course: { exclusiveEnterprise?: unknown }): boolean | null {
  if (course.exclusiveEnterprise === true || course.exclusiveEnterprise === "true") return true;
  if (course.exclusiveEnterprise === false || course.exclusiveEnterprise === "false") return false;
  return null;
}

/** Fare Retail → exclusiveEnterprise === false only. Any other selection → true only. null is excluded. */
export function filterCoursesByCatalog<T extends { exclusiveEnterprise?: unknown }>(
  courses: T[],
  catalogId: string = getCurrentEnterpriseId(),
): T[] {
  if (!catalogId) return [];
  const want = isFareRetailSelection(catalogId) ? false : true;
  return courses.filter((course) => exclusiveEnterpriseFlag(course) === want);
}

export function getCurrentEnterpriseId(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(CURRENT_ENTERPRISE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function setCurrentEnterpriseId(id: string): void {
  if (typeof window === "undefined") return;
  try {
    const previous = window.localStorage.getItem(CURRENT_ENTERPRISE_STORAGE_KEY) ?? "";
    window.localStorage.setItem(CURRENT_ENTERPRISE_STORAGE_KEY, id);
    if (previous !== id) {
      window.dispatchEvent(new CustomEvent(ENTERPRISE_CHANGED_EVENT, { detail: { enterpriseId: id } }));
    }
  } catch {
    // ignore storage errors
  }
}

// Tags a newly-created course onto the current (single) enterprise, if one
// is set — reads the enterprise's current courseIds and PUTs the new id
// appended on, the real backend field the /lms/enterprises/{id}/courses
// list endpoint below resolves against (no client-side mirroring needed).
export async function addCourseToCurrentEnterprise(courseId: string): Promise<void> {
  const enterpriseId = getCurrentEnterpriseId();
  if (!enterpriseId) return;

  const current = await enterpriseApi.get(enterpriseId);
  const existing = current.success ? current.data?.courseIds ?? [] : [];
  if (existing.includes(courseId)) return;

  try {
    await enterpriseApi.update(enterpriseId, { courseIds: [...existing, courseId] });
  } catch {
    // best-effort
  }
}

// ===================== Enterprise Member =====================

export type InvitationStatus = "PENDING" | "ACCEPTED";
export type EnterpriseMemberStatus = "ACTIVE" | "INACTIVE";

// Member-level access scope — not the same as roleAssignmentApi's
// ScopeType ("ENTERPRISE"/"DEPARTMENT"/"TEAM", which scopes a role
// assignment); this is a separate "how much of the enterprise's data can
// this member see" setting, not yet documented in the backend's Postman
// collection, so it's sent to the Enterprise Member API as a best-effort
// extra field alongside designation.
export type MemberScope = "Own Records" | "Reporting Hierarchy" | "Entire Enterprise";

export interface EnterpriseMember {
  id?: string;
  enterpriseId: string;
  userId?: string | null;
  email: string;
  invitationStatus?: InvitationStatus;
  departmentId?: string | null;
  reportsToMemberId?: string | null;
  primaryAdmin?: boolean;
  status?: EnterpriseMemberStatus;
  designation?: string | null;
  scope?: MemberScope | null;
  auditInfo?: AuditInfo;
}

export type EnterpriseMemberCreatePayload = {
  enterpriseId: string;
  email: string;
  departmentId?: string;
  reportsToMemberId?: string;
  designation?: string;
  scope?: MemberScope;
};

export type EnterpriseMemberUpdatePayload = Partial<
  Pick<EnterpriseMember, "departmentId" | "reportsToMemberId" | "primaryAdmin" | "status" | "designation" | "scope">
>;

export const enterpriseMemberApi = {
  listByEnterprise: (enterpriseId: string) =>
    enterpriseListRequest<EnterpriseMember>(`/lms/enterprise-members/enterprise/${enterpriseId}`),
  get: (id: string) => enterpriseRequest<EnterpriseMember>(`/lms/enterprise-members/${id}`),
  getByUser: (enterpriseId: string, userId: string) =>
    enterpriseRequest<EnterpriseMember>(
      `/lms/enterprise-members/enterprise/${enterpriseId}/user/${userId}`,
    ),
  create: (payload: EnterpriseMemberCreatePayload) =>
    enterpriseRequest<EnterpriseMember>("/lms/enterprise-members", "POST", payload),
  update: (id: string, payload: EnterpriseMemberUpdatePayload) =>
    enterpriseRequest<EnterpriseMember>(`/lms/enterprise-members/${id}`, "PUT", payload),
  // Invitee's own action — not surfaced in the Company Admin screens.
  accept: (id: string, userId: string) =>
    enterpriseRequest<EnterpriseMember>(
      `/lms/enterprise-members/${id}/accept?userId=${encodeURIComponent(userId)}`,
      "PUT",
    ),
  deactivate: (id: string) => enterpriseRequest<void>(`/lms/enterprise-members/${id}`, "DELETE"),
};

// ===================== Department =====================

export type DepartmentStatus = "ACTIVE" | "INACTIVE";

export interface Department {
  id?: string;
  enterpriseId: string;
  departmentName: string;
  departmentCode: string;
  description?: string;
  departmentHeadMemberId?: string;
  status?: DepartmentStatus;
  auditInfo?: AuditInfo;
}

export type DepartmentCreatePayload = {
  enterpriseId: string;
  departmentName: string;
  departmentCode: string;
  description?: string;
  departmentHeadMemberId?: string;
};

export type DepartmentUpdatePayload = Partial<
  Pick<Department, "departmentName" | "description" | "departmentHeadMemberId" | "status">
>;

export const departmentApi = {
  listByEnterprise: (enterpriseId: string) =>
    enterpriseListRequest<Department>(`/lms/departments/enterprise/${enterpriseId}`),
  get: (id: string) => enterpriseRequest<Department>(`/lms/departments/${id}`),
  create: (payload: DepartmentCreatePayload) =>
    enterpriseRequest<Department>("/lms/departments", "POST", payload),
  update: (id: string, payload: DepartmentUpdatePayload) =>
    enterpriseRequest<Department>(`/lms/departments/${id}`, "PUT", payload),
  remove: (id: string) => enterpriseRequest<void>(`/lms/departments/${id}`, "DELETE"),
};

// ===================== Team =====================

export type TeamStatus = "ACTIVE" | "INACTIVE";

export interface Team {
  id?: string;
  enterpriseId: string;
  departmentId: string;
  teamName: string;
  teamCode: string;
  description?: string;
  teamLeadMemberId?: string;
  status?: TeamStatus;
  auditInfo?: AuditInfo;
}

export type TeamCreatePayload = {
  enterpriseId: string;
  departmentId: string;
  teamName: string;
  teamCode: string;
  description?: string;
  teamLeadMemberId?: string;
};

export type TeamUpdatePayload = Partial<
  Pick<Team, "teamName" | "description" | "teamLeadMemberId" | "status">
>;

export const teamApi = {
  listByEnterprise: (enterpriseId: string) =>
    enterpriseListRequest<Team>(`/lms/teams/enterprise/${enterpriseId}`),
  listByDepartment: (departmentId: string) =>
    enterpriseListRequest<Team>(`/lms/teams/department/${departmentId}`),
  get: (id: string) => enterpriseRequest<Team>(`/lms/teams/${id}`),
  create: (payload: TeamCreatePayload) => enterpriseRequest<Team>("/lms/teams", "POST", payload),
  update: (id: string, payload: TeamUpdatePayload) =>
    enterpriseRequest<Team>(`/lms/teams/${id}`, "PUT", payload),
  remove: (id: string) => enterpriseRequest<void>(`/lms/teams/${id}`, "DELETE"),
};

// ===================== Enterprise Role Assignment =====================

export type ScopeType = "ENTERPRISE" | "DEPARTMENT" | "TEAM";
export type RoleAssignmentStatus = "ACTIVE" | "INACTIVE";

export interface EnterpriseRoleAssignment {
  id?: string;
  enterpriseId: string;
  memberId: string;
  roleId: string;
  scopeType: ScopeType;
  scopeId: string;
  status?: RoleAssignmentStatus;
  auditInfo?: AuditInfo;
}

export type RoleAssignmentCreatePayload = {
  enterpriseId: string;
  memberId: string;
  roleId: string;
  scopeType: ScopeType;
  scopeId: string;
};

export const roleAssignmentApi = {
  listByEnterprise: (enterpriseId: string) =>
    enterpriseListRequest<EnterpriseRoleAssignment>(
      `/lms/enterprise-role-assignments/enterprise/${enterpriseId}`,
    ),
  listByMember: (memberId: string) =>
    enterpriseListRequest<EnterpriseRoleAssignment>(
      `/lms/enterprise-role-assignments/member/${memberId}`,
    ),
  create: (payload: RoleAssignmentCreatePayload) =>
    enterpriseRequest<EnterpriseRoleAssignment>("/lms/enterprise-role-assignments", "POST", payload),
  remove: (id: string) =>
    enterpriseRequest<void>(`/lms/enterprise-role-assignments/${id}`, "DELETE"),
};

// ===================== Enterprise Member Assignment =====================
// Distinct from EnterpriseMember.departmentId — this is the full
// department/team assignment history (start/end dates, assignment type).

export type AssignmentType = "PRIMARY" | "SECONDARY";
export type MemberAssignmentStatus = "ACTIVE" | "INACTIVE";

export interface EnterpriseMemberAssignment {
  id?: string;
  enterpriseId: string;
  memberId: string;
  departmentId: string;
  teamId?: string | null;
  assignmentType: AssignmentType;
  startDate?: string;
  endDate?: string;
  status?: MemberAssignmentStatus;
  auditInfo?: AuditInfo;
}

export type MemberAssignmentCreatePayload = {
  enterpriseId: string;
  memberId: string;
  departmentId: string;
  teamId?: string;
  assignmentType: AssignmentType;
  startDate?: string;
};

export const memberAssignmentApi = {
  listByMember: (memberId: string) =>
    enterpriseListRequest<EnterpriseMemberAssignment>(
      `/lms/enterprise-member-assignments/member/${memberId}`,
    ),
  listByDepartment: (departmentId: string) =>
    enterpriseListRequest<EnterpriseMemberAssignment>(
      `/lms/enterprise-member-assignments/department/${departmentId}`,
    ),
  listByTeam: (teamId: string) =>
    enterpriseListRequest<EnterpriseMemberAssignment>(
      `/lms/enterprise-member-assignments/team/${teamId}`,
    ),
  create: (payload: MemberAssignmentCreatePayload) =>
    enterpriseRequest<EnterpriseMemberAssignment>(
      "/lms/enterprise-member-assignments",
      "POST",
      payload,
    ),
  remove: (id: string) =>
    enterpriseRequest<void>(`/lms/enterprise-member-assignments/${id}`, "DELETE"),
};

// ===================== Reporting Relation =====================
// Separate, backend-authoritative "who reports to whom" resource — distinct
// from EnterpriseMember.reportsToMemberId set at invite time.

export type HierarchyLevel =
  | "ASSOCIATE"
  | "MANAGER"
  | "LEADERSHIP"
  | "LEADERSHIP_CORE"
  | "DELIVERY_COACH"
  | "MASTER_CREATOR"
  | "COMPANY_ADMIN";

export interface ReportingRelation {
  id?: string;
  enterpriseId: string;
  memberId: string;
  reportsToMemberId: string;
  hierarchyLevel: HierarchyLevel | string;
  status?: "ACTIVE" | "INACTIVE";
  auditInfo?: AuditInfo;
}

export type ReportingRelationCreatePayload = {
  enterpriseId: string;
  memberId: string;
  reportsToMemberId: string;
  hierarchyLevel: HierarchyLevel | string;
};

export const reportingRelationApi = {
  listByEnterprise: (enterpriseId: string) =>
    enterpriseListRequest<ReportingRelation>(`/lms/reporting-relations/enterprise/${enterpriseId}`),
  create: (payload: ReportingRelationCreatePayload) =>
    enterpriseRequest<ReportingRelation>("/lms/reporting-relations", "POST", payload),
  remove: (id: string) => enterpriseRequest<void>(`/lms/reporting-relations/${id}`, "DELETE"),
};

// ===================== Content Access =====================

export type ContentType =
  | "COURSE"
  | "MODULE"
  | "LESSON"
  | "ASSESSMENT"
  | "QUIZ"
  | "MOCK"
  | "HABIT"
  | "SALES_PLAN";
export type ContentPermission = "VIEW" | "EDIT" | "EXPORT" | "DELETE";

export interface ContentAccess {
  id?: string;
  enterpriseId: string;
  contentId: string;
  contentType: ContentType | string;
  ownerMemberId: string;
  memberId: string;
  permissions: (ContentPermission | string)[];
  status?: "ACTIVE" | "INACTIVE";
  auditInfo?: AuditInfo;
}

export type ContentAccessCreatePayload = {
  enterpriseId: string;
  contentId: string;
  contentType: ContentType | string;
  ownerMemberId: string;
  memberId: string;
  permissions: (ContentPermission | string)[];
};

export const contentAccessApi = {
  listByMember: (memberId: string) =>
    enterpriseListRequest<ContentAccess>(`/lms/content-access/member/${memberId}`),
  create: (payload: ContentAccessCreatePayload) =>
    enterpriseRequest<ContentAccess>("/lms/content-access", "POST", payload),
  remove: (id: string) => enterpriseRequest<void>(`/lms/content-access/${id}`, "DELETE"),
};

// ===================== Roles (read-only) =====================
// Roles (Permissions, :8087) — the Role dropdown used when assigning an
// existing role to an Enterprise Member (below) only needs roleName/profileId,
// but the Roles admin screen (components/fare/screens/RolesScreen.tsx) needs
// the full record: a role's permission matrix over Category -> SubCategory ->
// Module Category -> Sub-Module (with Create/Read/Update/Delete booleans per
// sub-module), same shape as gully-properties-admin-portal-fe's
// lib/api/moduleMappingApi.ts Permission/PermissionPayload. Module
// Categories/Sub-Modules themselves are pre-existing shared definitions on
// that backend — this app only selects from them, it doesn't author new ones.

export interface ProfileType {
  id: string;
  type: string;
  description: string;
  status: string;
  applicationId: string;
}

export interface ModulePermission {
  moduleId: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

export interface RoleModuleCategoryPermissions {
  moduleCategoryId: string;
  modules: ModulePermission[];
}

export interface RoleSubModulePermission {
  subModuleId: string;
  subModuleName?: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

export interface Role {
  id?: string;
  roleName: string;
  profileId?: string;
  // Flat fields used on create/update payloads.
  categoryId?: string;
  subCategoryId?: string;
  categories?: string[];
  subCategories?: string[];
  moduleCategories?: RoleModuleCategoryPermissions[];
  // Nested fields returned by the full-details GET.
  category?: { id: string; name: string };
  subCategory?: { id: string; name: string };
  module?: { id: string; name: string | null };
  subModules?: RoleSubModulePermission[];
}

export type RolePayload = {
  roleName: string;
  profileId?: string;
  categories: string[];
  subCategories: string[];
  moduleCategories: RoleModuleCategoryPermissions[];
};

export interface ModuleCategory {
  id?: string;
  moduleCategoryName: string;
  subModuleId?: string[];
  subCategoryId?: string[];
}

export interface SubModule {
  id?: string;
  moduleName: string;
  moduleCategoryId?: string[];
}

export const profileTypeApi = {
  // GET :8087/profiles, filtered client-side to FARE's Application id
  // (FARE_APPLICATION_ID) so the dropdown only shows FARE-relevant profile
  // types (e.g. Tutor, Learner), not the whole platform.
  listForFare: async (): Promise<ApiResponse<ProfileType[]>> => {
    const res = await profileRequest<ProfileType[] | { data?: ProfileType[] }>("/profiles", "GET");
    if (!res.success) return res as ApiResponse<ProfileType[]>;
    const all = asArray<ProfileType>(res.data);
    return {
      success: true,
      data: all.filter((pt) => pt.applicationId === FARE_APPLICATION_ID),
      message: "Success",
    };
  },
};

export const roleApi = {
  // GET /permissions?profileId=... — every Role (Permission) available under
  // a given Profile Type. See profileTypeApi.listForFare to get profile ids.
  listByProfileType: async (profileId: string): Promise<ApiResponse<Role[]>> => {
    const res = await profileRequest<Role[] | { data?: Role[] }>(
      `/permissions?profileId=${encodeURIComponent(profileId)}`,
      "GET",
    );
    if (!res.success) return res as ApiResponse<Role[]>;
    return { success: true, data: asArray<Role>(res.data), message: "Success" };
  },
  // Unfiltered — used to resolve a roleId already assigned to a member back
  // to a display name, regardless of which profile type it came from.
  listAll: async (): Promise<ApiResponse<Role[]>> => {
    const res = await profileRequest<Role[] | { data?: Role[] }>("/permissions", "GET");
    if (!res.success) return res as ApiResponse<Role[]>;
    return { success: true, data: asArray<Role>(res.data), message: "Success" };
  },

  // GET /permissions/{id} — full permission matrix for one role (Roles
  // admin screen's edit view).
  getFullDetails: (id: string) => profileRequest<Role>(`/permissions/${id}`, "GET"),

  create: (payload: RolePayload) => profileRequest<Role>("/permissions", "POST", payload),

  update: (id: string, payload: RolePayload) =>
    profileRequest<Role>(`/permissions/${id}`, "PUT", { id, ...payload }),

  remove: (id: string) => profileRequest<void>(`/permissions/${id}`, "DELETE"),
};

export const moduleCategoryApi = {
  // GET /api/module-category — every Module Category on the shared backend
  // (pre-existing, not authored from this app).
  list: async (): Promise<ApiResponse<ModuleCategory[]>> => {
    const res = await profileRequest<ModuleCategory[] | { data?: ModuleCategory[] }>("/api/module-category", "GET");
    if (!res.success) return res as ApiResponse<ModuleCategory[]>;
    return { success: true, data: asArray<ModuleCategory>(res.data), message: "Success" };
  },

  // GET /submodules/module-category/{id} — the Sub-Modules under one Module
  // Category, used to build the Create/Read/Update/Delete permission table.
  getSubModules: async (moduleCategoryId: string): Promise<ApiResponse<SubModule[]>> => {
    const res = await profileRequest<SubModule[] | { data?: SubModule[] }>(
      `/submodules/module-category/${encodeURIComponent(moduleCategoryId)}`,
      "GET",
    );
    if (!res.success) return res as ApiResponse<SubModule[]>;
    return { success: true, data: asArray<SubModule>(res.data), message: "Success" };
  },
};
